// Defensive TypeaheadMenu implementation that prevents crashes when data is missing
// This can be loaded via script tag in index.html

export default function applyTypeaheadFixDefensive() {
  console.log('🚨 TYPEAHEAD FIX LOADED - VERSION 5.0 🚨');
  
  // Helper function to safely convert items to an array
  function safeToArray(items) {
    if (!items) return [];
    if (Array.isArray(items)) return items;
    return [items];
  }
  
  // Helper function to safely get option label
  function safeGetOptionLabel(option, labelKey) {
    try {
      if (!option) return '';
      if (typeof option === 'string') return option;
      if (typeof option === 'number') return String(option);
      if (labelKey) {
        if (typeof labelKey === 'function') {
          return labelKey(option) || '';
        }
        return option[labelKey] || '';
      }
      return option.label || option.name || option.value || '';
    } catch (error) {
      console.warn('[TypeaheadDebug] Error getting option label:', error);
      return '';
    }
  }
  
  // Helper function to safely wrap a component with error boundaries
  function wrapWithErrorBoundary(Component, componentName) {
    if (!Component) return null;
    
    // We can't easily recreate class components with error boundaries inside a functional wrapper
    // in this environment without generic React inheritance.
    // Instead, we will wrap the function call or render method.
    
    const WrappedComponent = function(props) {
      try {
        // Ensure props exist and are valid
        if (!props) {
          console.warn(`[TypeaheadDebug] No props provided to ${componentName}`);
          return null;
        }

        // Ensure options is always an array
        const safeOptions = safeToArray(props.options);
        
        // Create safe props with defaults
        const safeProps = Object.assign({}, props, {
          id: props.id || `${componentName.toLowerCase()}-${Math.random().toString(36).substr(2, 9)}`,
          options: safeOptions,
          emptyLabel: props.emptyLabel || 'No matches found',
          selected: safeToArray(props.selected),
          text: props.text || ''
        });
        
        // Handle labelKey specifically
        if (props.labelKey) {
           safeProps.labelKey = props.labelKey;
        }

        // Add component lifecycle logging
        if (window.DEBUG) {
          window.DEBUG.log(componentName, 'Rendering with props', {
            optionsCount: safeOptions.length,
            selectedCount: safeProps.selected.length,
            hasLabelKey: !!safeProps.labelKey
          });
        }

        // Call original component with safe props
        // Note: If Component is a class, we can't call it like a function.
        // We have to check if it's a class or function.
        // Since we are patching library internals, they might be classes.
        // If it is a class, we probably shouldn't wrap it this way unless we use React.createElement.
        
        // Checking if it is a class...
        if (Component.prototype && Component.prototype.isReactComponent) {
             // It's a class. We can't wrap it easily without JSX/React.createElement here.
             // But we are in a JS file. We need to use React.createElement if we want to return a component.
             // Or we just don't wrap classes this way.
             // The original script used Component(safeProps) which implies functional components or factory.
             
             // However, ReactBootstrapTypeahead usually exports classes.
             // The original script might have assumed functional components or was buggy?
             
             // Let's rely on React.createElement if window.React exists
             if (window.React) {
                 return window.React.createElement(Component, safeProps);
             }
        }
        
        return Component(safeProps);
      } catch (error) {
        console.warn(`[TypeaheadDebug] Error in ${componentName}:`, error);
        return null;
      }
    };

    // Copy over static properties
    Object.keys(Component).forEach(key => {
      WrappedComponent[key] = Component[key];
    });

    // Add display name for debugging
    WrappedComponent.displayName = `Safe${componentName}`;

    return WrappedComponent;
  }
  
  // Initialize the patch with retries
  let retryCount = 0;
  const MAX_RETRIES = 10;
  
  function patchTypeaheadMenu() {
    console.log(`🔍 Checking for ReactBootstrapTypeahead (attempt ${retryCount + 1}/${MAX_RETRIES})...`);
    
    // Check if React and ReactBootstrapTypeahead are available
    if (!window.React) {
      console.warn('⚠️ React not found');
      if (retryCount < MAX_RETRIES) {
        retryCount++;
        setTimeout(patchTypeaheadMenu, 500);
      }
      return;
    }
    
    if (!window.ReactBootstrapTypeahead) {
      console.warn('⚠️ ReactBootstrapTypeahead not found');
      if (retryCount < MAX_RETRIES) {
        retryCount++;
        setTimeout(patchTypeaheadMenu, 500);
      }
      return;
    }
    
    if (!window.ReactBootstrapTypeahead.TypeaheadMenu) {
      console.warn('⚠️ TypeaheadMenu component not found');
      if (retryCount < MAX_RETRIES) {
        retryCount++;
        setTimeout(patchTypeaheadMenu, 500);
      }
      return;
    }
    
    console.log('✅ Found ReactBootstrapTypeahead, applying patch...');
    
    try {
      // Store the original implementation
      const originalTypeaheadMenu = window.ReactBootstrapTypeahead.TypeaheadMenu;
      
      // Create a safe version of the menu item renderer
      const safeRenderMenuItem = function(option, props, index) {
        try {
          if (!option) {
            console.warn('[TypeaheadDebug] Attempted to render null menu item');
            return null;
          }
          
          const label = safeGetOptionLabel(option, props.labelKey);
          if (!label) {
            console.warn('[TypeaheadDebug] Could not get label for option:', option);
            return null;
          }
          
          // Ensure we have the original method
          if (!originalTypeaheadMenu.prototype._renderMenuItem) {
            console.warn('[TypeaheadDebug] Original _renderMenuItem not found');
            return null;
          }
          
          return originalTypeaheadMenu.prototype._renderMenuItem.call(this, option, props, index);
        } catch (error) {
          console.warn('[TypeaheadDebug] Error rendering menu item:', error);
          return null;
        }
      };
      
      // Wrap the component with error boundary
      const SafeTypeaheadMenu = wrapWithErrorBoundary(
        originalTypeaheadMenu,
        'TypeaheadMenu'
      );
      
      // Add the safe render method
      SafeTypeaheadMenu.prototype._renderMenuItem = safeRenderMenuItem;
      
      // Replace the original implementation
      window.ReactBootstrapTypeahead.TypeaheadMenu = SafeTypeaheadMenu;
      
      console.log('TypeaheadMenu patched with enhanced error handling');
      
      // Patch other components
      patchOtherComponents();
    } catch (error) {
      console.error('Error applying TypeaheadMenu patch:', error);
      if (retryCount < MAX_RETRIES) {
        retryCount++;
        setTimeout(patchTypeaheadMenu, 500);
      }
    }
  }
  
  // Try patching immediately
  patchTypeaheadMenu();
  
  // Add defensive wrappers for other components if they exist
  function patchOtherComponents() {
    if (window.ReactBootstrapTypeahead) {
      console.log('Adding additional defenses for Typeahead components');
      
      // Components to wrap with error boundaries
      const componentsToWrap = [
        'Typeahead',
        'AsyncTypeahead',
        'TypeaheadInputMulti',
        'Token',
        'TypeaheadInput',
        'TypeaheadInput', // Duplicate in list
        'TypeaheadMenuItem'
      ];
      
      componentsToWrap.forEach(componentName => {
        if (window.ReactBootstrapTypeahead[componentName]) {
          try {
            const original = window.ReactBootstrapTypeahead[componentName];
            window.ReactBootstrapTypeahead[componentName] = wrapWithErrorBoundary(
              original,
              componentName
            );
            console.log(`Patched ${componentName} component`);
          } catch (error) {
            console.warn(`Error patching ${componentName}:`, error);
          }
        }
      });
    }
  }
}



// WEBPACK FOOTER //
// ./src/utils/patches/typeaheadFixDefensive.js